<template>
  <div :class="getItemTableContainerClass">
    <ItemTable
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      :columns="columns"
      :details="details"
      :detailsButtonType="detailsButtonType"
      :detailsComponent="detailsComponent"
      :detailsIconLeft="detailsIconLeft"
      :handleRowClick="handleRowClick"
      :hasSelectColumn="true"
      :inDetailsAsidePanel="inDetailsAsidePanel"
      :itemType="itemType"
      :linkText="userGroupLinkText"
      :onMountedBeforeFetch="handleOnMountedBeforeFetch"
      :route="route"
      :selectionItemText="userGroupItemText"
      :showBound="true"
      :showSearchForm="true"
    />
    <AnnouncementWrapper
      v-if="announcement.enabled"
      caretPosition="right"
      :class="$style.announcement"
      :customCaretClassName="$style.announcementCaretPosition"
      :header="announcement.header"
      :intro="announcement.intro"
      :localStorageKey="announcement.localStorageKey"
      :mainActionLink="announcement.tooltipKbLink"
      mainActionText="Learn More"
      :text="announcement.text"
    >
      <a
        :class="$style.kbArticleLink"
        :href="announcement.linkValue"
        rel="noopener noreferrer"
        target="_blank"
      >{{ announcement.linkText }}</a>
    </AnnouncementWrapper>
  </div>
</template>
<script>
import {
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  ItemTypeRoutes,
  StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  xor,
} from 'lodash';
import AnnouncementWrapper from 'components/Announcement/AnnouncementWrapper.vue';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import LdapAsidePanelMixin from 'client/components/ItemTable/mixins/LdapAsidePanelMixin';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import RadiusViewAttributesButton from 'client/components/ItemTable/BodyComponents/RadiusViewAttributesButton.vue';
import pluralize from 'pluralize';

const userGroupCollection = StoreModuleMap[ItemTypeConsts.userGroup];

const { columnTypes } = ItemTableConsts;

export default {
  name: 'UserGroupItemTable',

  components: {
    AnnouncementWrapper,
    ItemTable,
  },

  mixins: [
    AsidePanelChildComponentPropsMixin,
    ItemTableComputedMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
    LdapAsidePanelMixin,
  ],

  props: {
    announcement: {
      type: Object,
      default() {
        return {
          enabled: false,
          header: '',
          intro: '',
          localStorageKey: '',
          text: '',
          tooltipKbLink: '',
        };
      },
    },
    directoryName: {
      default: '',
      type: String,
    },
    panelType: {
      default: ItemTableConsts.panelType.userAsidePanel,
      type: String,
    },
    showDistributionGroupEmail: {
      default: false,
      type: Boolean,
    },
    showType: {
      default: true,
      type: Boolean,
    },
    showUserGroupViewAttributesButton: {
      default: false,
      type: Boolean,
    },
    updateItemErrorHandler: {
      default: () => {},
      type: Function,
    },
    validation: {
      default: 'email',
      type: String,
    },
    watchSelectedItems: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data() {
    const columns = [
      {
        bodyComponent: 'ItemName',
        bodyComponentProps: {
          titleField: 'name',
          subtitleValue: 'Group of Users',
        },
        headingText: 'Group',
        sortBy: 'name',
      },
    ];

    if (this.showType) {
      columns.unshift({
        bodyComponent: 'GroupTypeIcon',
        classNames: {
          body: [columnTypes.iconColumn],
          heading: [columnTypes.iconColumn],
        },
        headingText: 'Type',
        sortBy: 'type',
      });
    }

    if (this.showUserGroupViewAttributesButton) {
      columns.push({
        bodyComponent: RadiusViewAttributesButton,
        classNames: {
          body: [columnTypes.viewReplyAttributesColumn],
          heading: [columnTypes.viewReplyAttributesColumn],
        },
      });
    }

    if (this.showDistributionGroupEmail) {
      columns.push({
        bodyComponent: 'EditableInputValidator',
        bodyComponentProps: {
          field: 'email',
          hideErrorValidation: true,
          placeholder: 'email@company.com',
          showNarrowInput: true,
          storeModule: userGroupCollection,
          type: 'email',
          updateItemErrorHandler: this.updateItemErrorHandler,
          validateDuplicates: true,
          validation: this.validation,
        },
        classNames: {
          body: [columnTypes.emailColumn],
          heading: [columnTypes.emailColumn],
        },
        headingText: 'Distribution Group Email',
        showHeadingTooltip: true,
        headingTooltipText: `Groups associated with this ${this.directoryName} Directory require the distribution email attribute be populated for JumpCloud to manage the group and its membership.`,
      });
    }

    if (this.inLdapDirectoryAside()) {
      columns.push({
        bodyComponent: 'SambaAuthentication',
        bodyComponentProps: {
          inLdapDirectoryAside: true,
        },
        headingText: 'Samba Authentication',
      });
    }

    const displayName = ItemTypeDisplayNames[ItemTypeConsts.userGroup];

    return {
      columns,
      itemType: ItemTypeConsts.userGroup,
      userGroupItemText: {
        label: 'bound',
        plural: pluralize(displayName),
        singular: displayName,
      },
    };
  },

  computed: {
    ...mapGetters(userGroupCollection, {
      getSelectedItems: 'getSelectedItems',
      selectedItemsCount: 'getSelectedItemsCount',
    }),

    ...mapState(userGroupCollection, {
      previouslySelectedItems: state => state.features.previouslySelectedItems.ids,
      selectedItems: state => state.features.selectedItems.ids,
      unselectedItems: state => state.features.unselectedItems.ids,
    }),

    route() {
      return ItemTypeRoutes[ItemTypeConsts.userGroup];
    },

    userGroupLinkText() {
      return pluralize(ItemTypeDisplayNames[ItemTypeConsts.userGroup]);
    },
  },

  watch: {
    async getSelectedItems(newSelectedItems) {
      const selectedItemsChanged = this.watchSelectedItems
      && newSelectedItems.length !== this.previouslySelectedItems.length;
      if (selectedItemsChanged) {
        const selectedItems = xor(newSelectedItems, this.previouslySelectedItems);
        this.$emit('selectedItemsChanged', selectedItems, 'selected');
      }
    },

    selectedItemsCount() {
      this.handleSelectedItemsCountChange();
      this.$emit('rowSelected', this.getCurrentSelectCount());
    },

    async unselectedItems(newUnselectedItems) {
      if (this.watchSelectedItems && newUnselectedItems.length) this.$emit('selectedItemsChanged', newUnselectedItems, 'unselected');
    },
  },

  methods: {
    ...mapActions(userGroupCollection, [
      'decorateUserGroups',
      'disableSambaGroup',
      'selectItem',
    ]),

    ...mapGetters(userGroupCollection, [
      'getItemsToUnbind',
      'getSambaEnabledGroupsToUpdate',
      'getSambaDisabledGroupsToUpdate',
      'isSambaEnabled',
    ]),

    displayUnbindingSambaLdapModal(unselectedSambaUserGroupIds) {
      return new Promise((resolve) => {
        let bodyText = 'This User Group has been enabled for Samba Authentication. Removing it from JumpCloud LDAP Directory will also remove Samba Authentication privileges.';

        if (unselectedSambaUserGroupIds.length > 1) {
          bodyText = 'One or more of the unselected User Groups have been enabled for Samba Authentication. Removing them from JumpCloud LDAP Directory will also remove Samba Authentication privileges.';
        }

        const componentProps = {
          headerText: 'Samba Authentication',
          bodyText,
          handleCancel: () => {
            ModalEventBus.close();
            resolve(false);
          },
          handleContinue: () => {
            ModalEventBus.close();
            resolve(true);
          },
          showClose: true,
        };

        ModalEventBus.open({
          isLargeModal: false,
          component: 'ConfirmationModal',
          componentProps,
        });
      });
    },

    getCurrentSelectCount() {
      return this.selectedItemsCount;
    },

    handleOnMountedBeforeFetch() {
      if (this.panelType === ItemTableConsts.panelType.directoryAsidePanel) {
        this.decorateUserGroups(true);
      }
    },

    /**
     * In the LDAP Directory Aside Panel when a User Group that has Samba
     * Authentication enabled is deselected then Samba Authentication needs to
     * be disabled. So in this case, display a confirmation dialog asking the
     * admin if they'd like to continue and if they choose to do so then the
     * sambaEnabled attribute will be set to false on the group. And if the
     * admin chooses to cancel then the unselected User Groups will be
     * reselected.
     */
    handleSelectedItemsCountChange() {
      if (!this.inLdapDirectoryAside()) {
        return;
      }

      const itemsToUnbind = this.getItemsToUnbind();

      if (itemsToUnbind.length) {
        const sambaItemIdsToUnbind = itemsToUnbind.reduce((acc, itemToUnbind) => {
          const { id } = itemToUnbind;

          if (this.isSambaEnabled(id)) {
            acc.push(id);
          }

          return acc;
        }, []);

        if (sambaItemIdsToUnbind.length > 0) {
          this.displayUnbindingSambaLdapModal(sambaItemIdsToUnbind)
            .then((continueUnbinding) => {
              if (continueUnbinding) {
                sambaItemIdsToUnbind.forEach(id => this.disableSambaGroup(id));
              } else {
                sambaItemIdsToUnbind.forEach(id => this.selectItem({ id }));
              }
            });
        }
      }
    },
  },
};
</script>
<style module>
@value (max-width-small,max-width-large,max-width-xxx-large) from '~css/breakpoints.css';

.announcement {
  left: 60%;
  position: absolute;
  top: 228px;
  width: 333px !important;
}

.announcementCaretPosition { right: 14px; }

.kbArticleLink {
  color: white !important; /* overriding `.jc-ux2 a` legacy styles */
  text-decoration: underline !important; /* overriding `.jc-ux2 a` legacy styles */
}

@media (max-width: max-width-small) {
  .announcement {
    left: 10% !important;
    top: 340px !important;
  }
}

@media (max-width: max-width-large) {
  .announcement {
    top: 270px;
  }
}

@media (max-width: max-width-xxx-large) {
  .announcement {
    left: 40%;
  }
}

</style>
